import * as React from 'react';

import {
  FinancialAccountType,
  PaymentAllocationMethod,
} from '@rematter/frontend-lib-api-client';

import { createEntityComboBox } from './helpers/createEntityComboBox';

import { createQueryHook } from '~/shared/providers/api';
import { QueryHookResult } from '~/shared/providers/api/createQueryHook';

export type SelectableFinancialAccount = QueryHookResult<
  typeof useFinancialAccountsQuery
>[number];

export const useFinancialAccountsQuery = createQueryHook(
  'shared.selectFinancialAccount',
  (params: {
    searchTerm?: string;
    ids?: string[];
    visibleInPayments?: boolean;
    facilityIDs?: string[];
    types?: FinancialAccountType[];
  }) => ({
    financialAccounts: {
      __args: {
        filter: {
          ids: params.ids,
          searchTerm: params.searchTerm,
          visibleInPayments: params.visibleInPayments,
          facilityIDs: params.facilityIDs,
          types: params.types,
        },
        pagination: {
          offset: 0,
          limit: 20,
        },
      },
      entries: {
        id: true,
        name: true,
        type: true,
        currency: true,
        paymentAllocationMethod: true,
        facility: {
          id: true,
          facilityName: true,
        },
      },
    },
  }),
  {
    select: (data) => data.financialAccounts.entries ?? [],
  },
);

const BaseSelectFinancialAccount = createEntityComboBox.remotelySearched(
  'SelectFinancialAccount',
  useFinancialAccountsQuery,
  {
    formatLabel: (entity) => entity.name,
    formatSubLabel: (entity) => `${entity.type} - ${entity.currency}`,
  },
);

type BaseProps = React.ComponentProps<typeof BaseSelectFinancialAccount>;

type SelectFinancialAccountProps = BaseProps & {
  onAccountSelect?: (
    account: {
      id: string;
      name: string;
      paymentAllocationMethod: PaymentAllocationMethod;
    } | null,
  ) => void;
};

export const SelectFinancialAccount = ({
  onAccountSelect,
  onChange,
  ...props
}: SelectFinancialAccountProps) => {
  const handleChange = React.useCallback(
    (
      value: string | undefined,
      entity?: SelectableFinancialAccount | undefined,
    ) => {
      onChange?.(value, entity);

      if (onAccountSelect) {
        if (entity) {
          onAccountSelect({
            id: entity.id,
            name: entity.name,
            paymentAllocationMethod: entity.paymentAllocationMethod,
          });
        } else {
          onAccountSelect(null);
        }
      }
    },
    [onChange, onAccountSelect],
  );

  return (
    <BaseSelectFinancialAccount
      {...props}
      onChange={handleChange}
      className="@SelectFinancialAccount"
    />
  );
};
